import { CommonModule } from '@angular/common';
import { Component, inject, OnInit } from '@angular/core';
import {
  FormBuilder,
  ReactiveFormsModule,
  Validators,
  FormControl,
  FormGroup,
} from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import {
  DialogService,
  DynamicDialogRef,
  DynamicDialogConfig,
} from 'primeng/dynamicdialog';
import { InputTextModule } from 'primeng/inputtext';
import { Select } from 'primeng/select';
import { FileUpload, FileUploadModule } from 'primeng/fileupload';
import { TextareaModule } from 'primeng/textarea';
import { HttpClientModule } from '@angular/common/http';
import { AuthService } from '@core/service/auth.service';
import { Empresa } from '@app/features-modules/bolsa-de-profissionais/api';
import { MessageService } from 'primeng/api';
import {
  EmpresaSectorService,
  TipoEmpresaService,
} from '@app/features-modules/bolsa-de-profissionais/services';
import { ApiService } from '@core/providers/http';
import { ToastModule } from 'primeng/toast';

@Component({
  selector: 'app-cadastrar-empresa',
  imports: [
    ReactiveFormsModule,
    DialogModule,
    Select,
    InputTextModule,
    ButtonModule,
    TextareaModule,
    CommonModule,
    FileUploadModule,
    HttpClientModule,
    ToastModule,
  ],
  providers: [
    DialogService,
    TipoEmpresaService,
    ApiService,
    EmpresaSectorService,
    MessageService,
  ],
  templateUrl: './cadastrar-empresa.component.html',
  styleUrls: ['./cadastrar-empresa.component.scss'],
  standalone: true,
})
export class CadastrarEmpresaComponent implements OnInit {
  private fb = inject(FormBuilder);
  private authService = inject(AuthService);
  private empresaService = inject(EmpresaSectorService);
  ref = inject(DynamicDialogRef);
  config = inject(DynamicDialogConfig);

  messageService = inject(MessageService);

  file: any;
  imagePreview: string | ArrayBuffer | null = null; // To store image as Base64 URL
  selectedFile: File | null = null;

  prefix = '';
  isEditMode = false;

  subSectorOptions: any[] = [
    { id: 1, designacao: 'Petróleo e Gás' },
    { id: 2, designacao: 'Recursos Minerais' },
  ];
  estadoContratoOptions: any[] = [
    { id: 1, designacao: 'Activo' },
    { id: 2, designacao: 'Inactivo' },
  ];

  tipoEmpresaOptions: any[] = [];
  activeStep: number = 1;
  institutionForm!: FormGroup;

  perfilEmpresaSector: any; // Recebe os dados do representante via Input

  constructor() {
    this.institutionForm = this.fb.group({
      id: ['0'],
      NIF: ['', Validators.required],
      Nome: [{ value: '', disabled: true }, Validators.required],
      SubSector: ['', Validators.required],
      URL: [''],
      Telefone: ['', Validators.required],
      EstadoContrato: ['', Validators.required],
      Email: ['', [Validators.required, Validators.email]],
      TipoEmpresa: ['', Validators.required],
      Localizacao: [{ value: '', disabled: true }],
      DescricaoEmpresa: ['', Validators.required],
      Foto: [''],
    }) as unknown as FormGroup<{
      [K in keyof Empresa]: FormControl<Empresa[K]>;
    }>;

    // Actualiza as validações
    this.institutionForm.get('NIF')?.updateValueAndValidity();

    this.tipoEmpresaOptions = this.config.data.tipoEmpresas;
  }

  ngOnInit(): void {
    if (this.config.data.perfilEmpresa && this.config.data.perfilEmpresa.id) {
      this.perfilEmpresaSector = this.config.data.perfilEmpresa;
      this.loadEmpresaDetails(this.perfilEmpresaSector);
      //this.patchValue(this.config.data.perfilEmpresa);
      this.isEditMode = true;
    } else {
      console.error('Empresa não foi passado para o componente!');
    }
  }

  get f() {
    return this.institutionForm.controls;
  }

  loadEmpresaDetails(perfilEmpresa: any): void {
    this.institutionForm.patchValue({
      id: perfilEmpresa.id ?? 0,
      NIF: perfilEmpresa.nif ?? '',
      Nome: perfilEmpresa.nome ?? '',
      SubSector: perfilEmpresa.subSector ?? '',
      URL: perfilEmpresa.url ?? '',
      Telefone: perfilEmpresa.telefone ?? '',
      EstadoContrato: perfilEmpresa.estadoContrato ?? '',
      Email: perfilEmpresa.email ?? '',
      TipoEmpresa: perfilEmpresa.tipoEmpresa ?? '',
      Localizacao: perfilEmpresa.localizacao ?? '',
      DescricaoEmpresa: perfilEmpresa.descricaoEmpresa ?? '',
      Foto: perfilEmpresa.Foto ?? '',
    });
    this.institutionForm.controls['NIF'].disable();

    // Define o preview da imagem se houver URL da foto
    this.empresaService
      .getPhotoEmpresaIdGet(perfilEmpresa.id)
      .subscribe((response) => {
        if (!response) return;

        if (!response || !response.type.startsWith('image/')) {
          console.warn('Resposta não é imagem. A usar placeholder.');
          this.imagePreview = null;
          return;
        }

        const file = new File([response], 'logo.jpg', {
          type: response.type,
        });
        this.selectedFile = file;

        const reader = new FileReader();
        reader.onload = () => {
          this.imagePreview = reader.result as string;
        };
        reader.readAsDataURL(file);

        this.institutionForm.patchValue({ Foto: file });
      });
  }

  save() {
    if (this.institutionForm.valid) {
      const formData = new FormData();
      const rawData = this.institutionForm.getRawValue() as Empresa;

      // Clona o objeto e remove o campo 'Foto' antes de gerar o payload
      const { Foto, ...empresaSemFoto } = rawData;

      // 1. Adiciona o objeto sem 'Foto' como JSON no campo 'payload'
      formData.append('payload', JSON.stringify(empresaSemFoto));

      // 2. Adiciona o arquivo como 'File', se houver
      if (this.selectedFile) {
        formData.append('File', this.selectedFile, this.selectedFile.name);
      }
      for (const pair of formData.entries()) {
        console.log(`${pair[0]}:`, pair[1]);
      }

      // 3. Fecha o diálogo passando o formData
      this.ref.close(formData);
    } else {
      this.institutionForm.markAllAsTouched();
      this.messageService.add({
        severity: 'error',
        summary: 'Erro',
        detail: 'Preencha todos os campos obrigatórios corretamente.',
        life: 3000,
      });
    }
  }

  /*
  save() {
    if (this.institutionForm.valid) {
      const formData = new FormData();
      const rawData = this.institutionForm.getRawValue() as Empresa;

      (Object.keys(rawData) as (keyof Empresa)[]).forEach(key => {
        if (key !== 'Foto') {
          const value = rawData[key];
          if (value !== null && value !== undefined) {
            formData.append(key, value.toString());
          }
        }
      });

      if (this.selectedFile) {
        formData.append('Foto', this.selectedFile, this.selectedFile.name);
      } else if (typeof rawData.Foto === 'string' && rawData.Foto.trim() !== '') {
        formData.append('FotoURL', rawData.Foto); // opção caso backend suporte enviar URL
      }

      console.log('FormData pronto para envio:');
      for (const pair of formData.entries()) {
        console.log(`${pair[0]}: ${pair[1]}`);
      }

      this.ref.close(formData);
    } else {
      this.messageService.add({
        severity: 'error',
        summary: 'Erro',
        detail: 'Preencha todos os campos obrigatórios corretamente.',
        life: 3000
      });
    }
  }*/

  cancel() {
    this.ref.close(); // Close the dialog without saving
  }

  choose(event: any, callback: () => void) {
    callback();
  }

  onSelectedFiles(event: { currentFiles: any }) {
    this.file = event.currentFiles[0];

    //Adicionar validação de tamanho da imagem (ex: máx. 2MB)
    const maxSizeMB = 2;
    if (this.file.size > maxSizeMB * 1024 * 1024) {
      this.messageService.add({
        severity: 'error',
        summary: 'Imagem muito grande',
        detail: `A imagem deve ter no máximo ${maxSizeMB}MB.`,
        life: 3000,
      });
      return;
    }

    const isImage = this.file.type.startsWith('image/');
    if (!isImage) {
      this.messageService.add({
        severity: 'error',
        summary: 'Formato inválido',
        detail: 'Apenas imagens são permitidas.',
        life: 3000,
      });
      return;
    }

    if (this.file) {
      this.selectedFile = this.file;
      this.institutionForm.patchValue({ foto: this.file });

      const reader = new FileReader();
      reader.onload = () => {
        this.imagePreview = reader.result as string;
      };
      reader.readAsDataURL(this.file);
    }
    /*this.file = event.currentFiles[0];
    console.log(this.file);
    const reader = new FileReader();
    reader.onload = () => {
      this.imagePreview = reader.result; // Store Base64 URL
    };
    reader.readAsDataURL(this.file);*/
  }

  searchNif() {
    const nif = (this.institutionForm.controls['NIF'].value || '')
      .toString()
      .trim();
    if (!nif) return;

    this.authService.consultarBi(nif).subscribe({
      next: (result) => {
        console.log(result);
        const isSuccess =
          result?.retorno?.codigo === 200 || result?.data?.code === 200;

        if (isSuccess) {
          const d = result?.data?.data || result?.data || {};

          const firstName = (d.first_name || d.nome || '').trim();
          const lastName = (d.last_name || d.sobrenome || '').trim();
          const nome =
            `${firstName} ${lastName}`.trim() ||
            d.nomeColectivo ||
            d.nomeContribuinte ||
            '';

          const enderecoPartes = [
            d.residence_address?.replace(/,\s*$/, ''),
            d.residence_neighbor,
            d.residence_commune_name,
            d.residence_municipality_name,
            d.residence_province_name,
          ]
            .map((p) => (typeof p === 'string' ? p.trim() : ''))
            .filter((p) => p && p.toUpperCase() !== 'NA');

          const localizacao =
            enderecoPartes.join(', ') ||
            (d.moradaPrincipal
              ? `${d.moradaPrincipal.municipio || ''} ${d.moradaPrincipal.bairro || ''} ${d.moradaPrincipal.morada || ''}`.trim()
              : '');

          const email =
            d.email ||
            d.listaReprLegal?.reprLegal?.[0]?.emailRepLegal ||
            d.listaEmail?.email?.[0] ||
            '';

          const telefone =
            d.telefone ||
            d.numeroContacto ||
            d.contactoPrincipal?.numeroContacto ||
            '';

          // Seta apenas os dados retornados
          const patchValues: any = {};
          if (nome) {
            patchValues.Nome = nome;
          }
          if (localizacao) {
            patchValues.Localizacao = localizacao;
          }
          if (email) {
            patchValues.Email = email;
          }
          if (telefone) {
            patchValues.Telefone = telefone;
          }

          this.institutionForm.patchValue(patchValues);

          // Habilita os outros campos que não vieram retornados
          if (!nome) {
            this.institutionForm.controls['Nome'].enable();
          }
          if (!localizacao) {
            this.institutionForm.controls['Localizacao'].enable();
          }
          if (!email) {
            this.institutionForm.controls['Email'].enable();
          }
          if (!telefone) {
            this.institutionForm.controls['Telefone'].enable();
          }
        } else {
          this.messageService.add({
            severity: 'warn',
            summary: 'NIF não encontrado',
            detail: 'Nenhuma informação foi encontrada para o NIF fornecido.',
            life: 4000,
          });
          this.institutionForm.controls['Nome'].enable();
          this.institutionForm.controls['Localizacao'].enable();
        }
      },
      error: (err) => {
        console.error('Erro ao consultar NIF:', err);
        this.messageService.add({
          severity: 'error',
          summary: 'Erro ao consultar NIF',
          detail: 'Falha ao consultar o NIF. Tente novamente.',
          life: 4000,
        });
        this.institutionForm.controls['Nome'].enable();
        this.institutionForm.controls['Localizacao'].enable();
      },
    });
  }
}
